/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #activeSource = null;
  #savedScrollLeft = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleOpen, opts);
    // Dialog close does not bubble, so listen in the capture phase.
    this.addEventListener('close', this.#handleClose, { ...opts, capture: true });

    this.#keepFirstPhotoOnLoad(opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  // Phones should always open a product on its first photo. Mobile Safari can
  // re-snap a long swipeable gallery partway through while lazy photos load or
  // scroll positions are restored, so until the shopper touches the gallery,
  // any scroll that did not come from them is put back to the first photo.
  #keepFirstPhotoOnLoad(opts) {
    const gallery = this.querySelector('[data-product-gallery]');
    if (!gallery) return;

    let shopperMoved = false;
    // Snapping stays off until the shopper swipes, so the browser cannot
    // re-snap the gallery to another photo while the page is loading.
    const enableSnap = () => gallery.removeAttribute('data-snap-pending');
    const markShopper = () => { shopperMoved = true; enableSnap(); };
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
      gallery.addEventListener(type, markShopper, { ...opts, passive: true });
    }

    const backToFirst = () => {
      if (shopperMoved || gallery.scrollWidth <= gallery.clientWidth) return;
      if (Math.abs(gallery.scrollLeft) > 1) gallery.scrollTo({ left: 0, behavior: 'instant' });
    };

    const guardUntil = performance.now() + 5000;
    gallery.addEventListener('scroll', () => {
      if (performance.now() < guardUntil) backToFirst();
    }, { ...opts, passive: true });

    requestAnimationFrame(backToFirst);
    window.addEventListener('load', backToFirst, { ...opts, once: true });
    window.addEventListener('pageshow', backToFirst, opts);
    for (const delay of [250, 1000, 2500]) setTimeout(backToFirst, delay);
    setTimeout(() => { backToFirst(); enableSnap(); }, 6000);
  }

  // Remember which gallery photo was tapped so closing the zoom returns to it.
  // Mobile browsers do not focus tapped buttons, so the dialog focus restore
  // could otherwise scroll the swipeable gallery to a different photo.
  #handleOpen = (event) => {
    if (!(event.target instanceof Element)) return;
    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;
    this.#activeSource = source;
    this.#savedScrollLeft = this.querySelector('[data-product-gallery]')?.scrollLeft ?? null;
    this.#syncZoomImage(source);
    // Focus the tapped photo before the zoom opens so the dialog remembers the right one.
    source.focus({ preventScroll: true });
  };

  #handleClose = (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    const source = this.#activeSource;
    const savedLeft = this.#savedScrollLeft;
    this.#activeSource = null;
    this.#savedScrollLeft = null;
    if (!source || source.getAttribute('commandfor') !== event.target.id) return;

    const gallery = this.querySelector('[data-product-gallery]');
    // Mobile Safari can re-snap the swipeable gallery to a different photo while
    // the page scroll lock is released, so pause snapping and pin the tapped
    // photo until the close fade has finished.
    const restore = () => {
      if (!gallery || gallery.scrollWidth <= gallery.clientWidth) return;
      const slide = [...gallery.children].find((child) => child.contains(source));
      let left = savedLeft;
      if (slide) {
        left = gallery.scrollLeft + slide.getBoundingClientRect().left - gallery.getBoundingClientRect().left;
      }
      if (left != null) gallery.scrollTo({ left, behavior: 'instant' });
    };

    if (gallery) gallery.style.scrollSnapType = 'none';
    source.focus({ preventScroll: true });
    requestAnimationFrame(restore);
    for (const delay of [120, 360]) setTimeout(restore, delay);
    setTimeout(() => {
      restore();
      if (gallery) gallery.style.removeProperty('scroll-snap-type');
    }, 600);
  };

  // Always show the photo that was actually tapped. The enlarged view copies the
  // tapped thumbnail's image sources, so it can never open on a different photo.
  #syncZoomImage(source) {
    const thumb = source.querySelector('img');
    const dialogId = source.getAttribute('commandfor');
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    const zoom = dialog?.querySelector('img[data-zoom-image]');
    if (!(thumb instanceof HTMLImageElement) || !(zoom instanceof HTMLImageElement)) return;

    const thumbKey = (thumb.currentSrc || thumb.src).split('?')[0].replace(/_\d+x\d*(?=\.)/, '');
    const zoomKey = zoom.src.split('?')[0].replace(/_\d+x\d*(?=\.)/, '');
    if (thumbKey && thumbKey === zoomKey) return;

    zoom.loading = 'eager';
    if (thumb.srcset) zoom.srcset = thumb.srcset;
    else zoom.removeAttribute('srcset');
    zoom.sizes = '100vw';
    zoom.src = thumb.currentSrc || thumb.src;
    if (thumb.alt) zoom.alt = thumb.alt;
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
